import PreferencesService from '../services/PreferencesService'
import { Temporal } from '@js-temporal/polyfill'

import { createVuetify } from 'vuetify'

import { aliases, mdi } from 'vuetify/iconsets/mdi-svg'
import {
  mdiAccount,
  mdiAccountGroup,
  mdiAccountMultiple,
  mdiAlert,
  mdiAlertCircleOutline,
  mdiAlertDecagramOutline,
  mdiAlphaSCircle,
  mdiArrowDownBold,
  mdiArrowRightThin,
  mdiArrowUpBold,
  mdiAsterisk,
  mdiAutoFix,
  // mdiClockTimeFour,
  mdiCalendar,
  mdiCalendarRange,
  mdiCallSplit,
  mdiCancel,
  mdiCar,
  mdiCashMultiple,
  mdiChartLine,
  mdiChartPie,
  mdiCheckBold,
  mdiChevronDownCircle,
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUpCircle,
  mdiCircleOutline,
  mdiClipboardText,
  mdiClipboardTextClock,
  mdiClockTimeTwelveOutline,
  mdiClose,
  mdiCog,
  mdiCubeOutline,
  mdiCurrencyUsd,
  mdiDelete,
  mdiDotsHorizontal,
  mdiDownload,
  mdiEye,
  mdiFilter,
  mdiFilterOutline,
  mdiHelpCircle,
  mdiHome,
  mdiLandRowsVertical,
  mdiLockOpenOutline,
  mdiKey,
  mdiMinus,
  mdiNumeric,
  mdiOpenInNew,
  mdiParking,
  mdiPencil,
  mdiPiggyBankOutline,
  mdiPipe,
  mdiPlus,
  mdiPower,
  mdiPowerOff,
  // mdiRecordCircle,
  mdiReload,
  mdiRestore,
  mdiSummit,
  mdiStadium,
  mdiTextBoxOutline,
  mdiTimerOutline,
  mdiViewList,
} from '@mdi/js'

// import '@mdi/font/css/materialdesignicons.css' // Ensure you are using css-loader

// Ocra brand colors:
// https://www.notion.so/ocra/Ocra-Brand-Assets-Logo-Files-89f0a2dd9e334372a2c871dd52bcd8e0
const colors = {
  logo_purple: '#471488', // Logo, headline text
  grape_purple: '#9a77db', // Subhead text, primary accent color
  pastel_purple: '#e1e2ff', // Primary accent color
  highlighter_magenta: '#cb6ce5', // Primary CTA buttons (w/ white text)
  lake_blue: '#2c95eb', // Secondary CTA buttons (w/ white text)
  black: '#101319', // Body text
  white: '#ffffff', // Background
  pastel_yellow: '#feffae', //  Organic accent elements
  highlighter_yellow: '#f9ff00', //  Organic accent elements
  pastel_blue: '#a2daf8', //  Organic accent elements
  pastel_magenta: '#edb0f1', // Organic accent elements
}

/**
 * This wrapper function allows main.ts to pass dynamic config to createVuetify().
 */
export function configureVuetify(
  globalColors: {[key: string]: string},
) {

  // Object.values(vuetify.theme.themes.value).forEach(theme => {
  //   Object.assign(theme.colors, globalColors)
  // })

  return createVuetify({
    defaults: {
      VBtn: {
        color: 'cta-secondary-background',
        variant: 'flat', // TODO: this isn't being applied to buttons in modals (maybe because they're technically outside of VApp?)
      },
      VBtnGroup: {
        // Annoyingly, VBtn and VBtnGroup have different heights with density="default" (32px and 48px, respectively).
        // Force VBtnGroup to use density="compact" by default, so it aligns with VBtn.
        density: 'compact',
      },
      VBtnToggle: {
        // Annoyingly, VBtn and VBtnToggle have different heights with density="default" (32px and 48px, respectively).
        // Force VBtnToggle to use density="compact" by default, so it aligns with VBtn.
        density: 'compact',
      },
      VCalendar: {
        weekdayFormat: (timestamp: any) => Temporal.PlainDate.from(timestamp.date).toLocaleString('en-US', {weekday: 'narrow'}),
      },
      VCard: {
        variant: 'outlined',
      },
      VDateInput: {
        prependIcon: '',
      },
      VFileInput: {
        prependIcon: '',
      },
      VNumberInput: {
        controlVariant: 'hidden',
      },
      VTabs: {
        color: 'tab-highlighted',
      },
      VTextField: {
        hideSpinButtons: true,
      },
    },
    icons: {
      defaultSet: 'mdi',
      aliases: {
        // https://vuetifyjs.com/en/features/icon-fonts/#material-design-icons-js-svg
        ...aliases,
        actionMenuButton: mdiDotsHorizontal,
        alert: mdiAlert,
        authenticate: mdiLockOpenOutline,
        autodetect: mdiAutoFix,
        averageRate: mdiCurrencyUsd,
        back: mdiChevronLeft,
        blackout: mdiCancel,
        bookingDuration: mdiTimerOutline,
        bookingPage: mdiTextBoxOutline,
        breadcrumbSeparator: mdiChevronRight,
        calendarEvent: mdiCircleOutline, // mdiRecordCircle,
        calendarPrev: mdiChevronLeft,
        calendarNext: mdiChevronRight,
        close: mdiClose,
        code: mdiNumeric,
        collapseHighlighted: mdiChevronUpCircle,
        confirm: mdiCheckBold,
        create: mdiPlus,
        customer: mdiAccount,
        dateSelectionModeSingle: mdiCalendar,
        dateSelectionModeRange: mdiCalendarRange,
        defaultRate: mdiCurrencyUsd,
        delete: mdiDelete,
        delta: mdiArrowRightThin,
        disabled: mdiPowerOff,
        download: mdiDownload,
        edit: mdiPencil,
        enabled: mdiPower,
        error: mdiAlertCircleOutline,
        expandHighlighted: mdiChevronDownCircle,
        filter: mdiFilterOutline,
        filterActive: mdiFilter,
        help: mdiHelpCircle,
        history: mdiClipboardTextClock,
        inventoryBreakdown: mdiChartPie,
        kpi: mdiAlertDecagramOutline,
        location: mdiHome,
        log: mdiClipboardText,
        metricDecreased: mdiArrowDownBold,
        metricIncreased: mdiArrowUpBold,
        midnight: mdiClockTimeTwelveOutline,
        newWindow: mdiOpenInNew,
        ok: mdiCheckBold,
        organization: mdiAccountGroup,
        parcs: mdiPipe,
        password: mdiKey,
        peak: mdiSummit,
        product: mdiParking,
        rateManagement: mdiCashMultiple,
        reload: mdiReload,
        remove: mdiMinus,
        report: mdiChartLine,
        reservation: mdiCar,
        reset: mdiRestore,
        revenue: mdiPiggyBankOutline,
        setAll: mdiAsterisk,
        settings: mdiCog,
        spotRate: mdiAlphaSCircle, // mdiClockTimeFour,
        solution: mdiLockOpenOutline,
        syncRates: mdiCallSplit,
        taxAndFee: mdiCurrencyUsd,
        universalInventory: mdiCubeOutline,
        universalRatesAndInventory: mdiViewList,
        user: mdiAccount,
        users: mdiAccountMultiple,
        userSolutionsNone: mdiCancel,
        venue: mdiStadium,
        vertical: mdiLandRowsVertical,
        view: mdiEye,
      },
      sets: {
        mdi,
      }
    },
    theme: {
      // https://vuetifyjs.com/en/features/theme/#custom-theme-colors
      defaultTheme: PreferencesService.preferences.darkMode ? 'dark' : 'light',
      variations: {
        colors: Object.keys(globalColors),
        lighten: 5,
        darken: 5,
      },
      themes: {

        light: {
          dark: false,
          colors: Object.assign({

            // color names which are part of the default theme
            // primary: '#471488',
            background: colors.white,
            'on-background': colors.black,
            secondary: colors.lake_blue, // links

            // custom color names
            headings: colors.logo_purple,
            'app-bar-item-foreground': colors.logo_purple,
            'action-foreground': colors.logo_purple,
            'cta-primary-background': colors.highlighter_magenta,
            'cta-primary-foreground': colors.white,
            'cta-secondary-background': colors.lake_blue,
            'cta-secondary-foreground': colors.white,
            'modal-title-background': colors.logo_purple,
            'nav-item-active': colors.logo_purple,
            'nav-item-icon-foreground': colors.logo_purple,
            'pagination-button-foreground': colors.logo_purple,
            'tab-highlighted': colors.logo_purple,
            'table-stripe-background': '#f4f4f4',

          }, globalColors),
        },

        dark: {
          dark: true,
          colors: Object.assign({

            // color names which are part of the default theme
            // primary: '#471488',
            // background: colors.white,
            // 'on-background': colors.black,
            secondary: colors.lake_blue, // links

            // custom color names
            headings: colors.grape_purple,
            'app-bar-item-foreground': colors.grape_purple,
            'action-foreground': colors.grape_purple,
            'cta-primary-background': colors.highlighter_magenta,
            'cta-primary-foreground': colors.white,
            'cta-secondary-background': colors.lake_blue,
            'cta-secondary-foreground': colors.white,
            'modal-title-background': colors.logo_purple,
            'nav-item-active': colors.grape_purple,
            'nav-item-icon-foreground': colors.grape_purple,
            'pagination-button-foreground': colors.grape_purple,
            'tab-highlighted': colors.grape_purple,
            'table-stripe-background': '#282828',

          }, globalColors),
        },
      },
    },
  })

}
